/**
 * WP-A (Gap B) — Pure, transport-free projection of governed evidence facts into
 * product vocabulary. It contains NO eligibility logic: `accepted` here is a
 * label for what the backend already decided, never permission to verify.
 */

import type { EvidenceListEntry } from "../evidencePort";

/* --------------------------------------------------------- product states */

export type EvidenceProductState =
  "uploading" | "processing" | "accepted" | "rejected" | "quarantined" | "removed" | "insufficient";

/**
 * The governed record state, projected to product vocabulary. This is a LABEL,
 * not a decision: nothing in the app may treat `accepted` here as permission to
 * verify a condition.
 */
export function evidenceProductState(entry: EvidenceListEntry): EvidenceProductState {
  const record = entry.record;
  if (record.state === "deleted" || record.objectPurged) return "removed";
  if (record.sufficiencyState === "insufficient") return "insufficient";
  switch (record.state) {
    case "pending_upload":
    case "uploaded":
      return "uploading";
    case "processing":
      return "processing";
    case "accepted":
      return "accepted";
    case "rejected":
      return "rejected";
    case "quarantined":
    default:
      return "quarantined";
  }
}

/** One safe sentence per state. No provider text, no scanner payload, no path. */
export function evidenceStateCopy(state: EvidenceProductState): {
  label: string;
  detail: string;
} {
  switch (state) {
    case "uploading":
      return { label: "Uploading", detail: "The file is being stored securely." };
    case "processing":
      return { label: "Checking", detail: "Contents and safety checks are running." };
    case "accepted":
      return { label: "Accepted", detail: "Checks passed. It can support a resolution." };
    case "rejected":
      return {
        label: "Rejected",
        detail: "This file cannot be used. Try a PDF, PNG, JPEG or text file under 10 MB.",
      };
    case "quarantined":
      return {
        label: "Held for safety checks",
        detail: "Safety checks did not clear this file, so it cannot support a resolution.",
      };
    case "insufficient":
      return { label: "Marked insufficient", detail: "This file cannot support a resolution." };
    case "removed":
    default:
      return { label: "Removed", detail: "The file is no longer available." };
  }
}

/** Retry is offered ONLY where the backend contract permits another attempt. */
export function evidenceRetryAllowed(entry: EvidenceListEntry): boolean {
  return entry.record.state === "quarantined";
}
